import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, ArrowLeft, Shield, ChevronRight } from 'lucide-react';
import { ADD_ONS } from '../data/constants';

// Condition add-ons on top of the Alchemist Reset — same catalog and prices as
// the booking flow. Steam and deep shampoo are already in the base, so they
// are not offered again here.
const RESET_ADD_ONS = ADD_ONS.filter((a) => a.id !== 'steam_clean' && a.id !== 'carpet_shampoo');

const BASELINE_INCLUDES = [
  'Full vacuum — seats, carpet, floor mats & trunk',
  'Full steam clean of all interior surfaces',
  'Deep carpet & seat shampoo — machine extraction',
  'Dashboard, console & door panel deep shampoo',
  'Cup holders, vents & all crevices',
  'All interior windows streak-free',
  'Door & trunk jamb deep clean',
  'Fabric or leather shampoo & conditioning',
  'Full headliner wipe-down',
  'UV protectant on dash & plastics',
  'Final walk-through handoff',
];

const MONTHLY_INCLUDES = [
  'Full vacuum — seats, carpet, floor mats & trunk',
  'Wipe-down of dash, console & door panels',
  'Cup holders & vents cleaned',
  'Interior windows streak-free',
  'Spot steam — any new stains handled same visit',
  'UV protectant refresh',
  'Light deodorize & freshen',
];

export default function InteriorMaintenancePage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('monthly');

  return (
    <div className="min-h-screen bg-cloud text-[#111827] pb-28">

      {/* Back */}
      <div className="px-6 pt-6 max-w-3xl mx-auto">
        <button
          onClick={() => navigate('/detail')}
          className="inline-flex items-center gap-2 text-sm text-[#6B7280] hover:text-[#111827] transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
      </div>

      {/* Hero */}
      <section className="px-6 pt-8 pb-10 max-w-3xl mx-auto">
        <p className="text-[#68caf1] text-xs font-bold uppercase tracking-widest mb-3">Interior Maintenance Program</p>
        <h1 className="text-4xl md:text-5xl font-bold mb-4 leading-tight">
          Always Fresh.<br />
          <span className="text-[#68caf1]">from $75 / Month.</span>
        </h1>
        <p className="text-[#6B7280] text-lg leading-relaxed max-w-xl">
          Start with the Alchemist Reset to bring your car to pristine — then from $75 a month keeps it there. No upsells, no condition-based charges.
        </p>
      </section>

      {/* Top CTA */}
      <section className="px-6 pb-10 max-w-3xl mx-auto">
        <button
          onClick={() => navigate('/maintenance/book')}
          className="w-full bg-[#68caf1] text-black font-bold text-base py-4 rounded-2xl hover:bg-[#5bb8db] transition-all shadow-lg active:scale-95 touch-manipulation"
        >
          Book the Alchemist Reset — from $200
        </button>
        <p className="text-center text-xs text-[#9CA3AF] mt-2">50% deposit today · Sedan $200 · SUV $215 · Truck $225 · Then from $75/month after</p>
      </section>

      {/* How it works */}
      <section className="px-6 pb-10 max-w-3xl mx-auto">
        <div className="overflow-x-auto -mx-6 px-6">
          <div className="grid grid-cols-3 gap-4 min-w-[520px]">
            {[
              {
                num: '1',
                title: 'Alchemist Reset',
                price: 'from $200',
                body: 'Full steam, carpet shampoo, deep clean, windows, UV protect — brings your car from wherever it is to pristine. Add-ons for condition extras available during booking.',
              },
              {
                num: '2',
                title: 'Monthly Maintenance',
                price: 'from $75 / month',
                body: 'We come back every month — vacuum, wipe-down, spot steam, windows. Fast because the car is already clean.',
              },
              {
                num: '3',
                title: 'Your Rate. Always.',
                price: 'Monthly: Sedan $75 · SUV $80 · Truck $85',
                body: "Rough month? Kids? We handle it. Your rate never changes based on condition — that's the whole point.",
              },
            ].map(({ num, title, price, body }) => (
              <div key={num} className="bg-white border border-gray-200 rounded-2xl p-6 flex flex-col">
                <p className="text-5xl font-black text-[#68caf1] leading-none mb-4">{num}</p>
                <p className="font-bold text-[#111827] text-base mb-0.5">{title}</p>
                <p className="text-[#68caf1] text-xs font-bold uppercase tracking-widest mb-3">{price}</p>
                <p className="text-[#6B7280] text-sm leading-relaxed flex-1">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Flat rate guarantee */}
      <section className="px-6 pb-10 max-w-3xl mx-auto">
        <div className="bg-[#111827] rounded-2xl p-6 text-white flex gap-4">
          <Shield className="w-6 h-6 text-[#68caf1] flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-bold text-white mb-1">Your rate is always your rate.</p>
            <p className="text-gray-400 text-sm leading-relaxed">
              Sedan $75 · SUV $80 · Truck $85 — and that includes spot steam, light extraction, whatever it takes to make it right for a loyal client. Rough month? Handled. Akeem won't nickel-and-dime you for doing his job. If something is truly beyond the scope of a standard visit, he'll let you know before he starts and discuss a one-time add-on. That's the honest version of flat rate.
            </p>
          </div>
        </div>
      </section>

      {/* What's included — tabbed */}
      <section className="px-6 pb-10 max-w-3xl mx-auto">
        <h2 className="text-xl font-bold mb-4">What's Included</h2>
        <div className="flex bg-gray-100 rounded-xl p-1 mb-5 w-full max-w-xs">
          <button
            onClick={() => setTab('monthly')}
            className={`flex-1 py-2 text-sm font-bold rounded-lg transition-all ${tab === 'monthly' ? 'bg-white text-[#111827] shadow-sm' : 'text-[#6B7280]'}`}
          >
            Monthly Visit
          </button>
          <button
            onClick={() => setTab('baseline')}
            className={`flex-1 py-2 text-sm font-bold rounded-lg transition-all ${tab === 'baseline' ? 'bg-white text-[#111827] shadow-sm' : 'text-[#6B7280]'}`}
          >
            Reset Visit
          </button>
        </div>
        <div className="bg-white border border-gray-200 rounded-2xl p-6">
          {tab === 'monthly' ? (
            <>
              <p className="text-xs font-bold text-[#68caf1] uppercase tracking-widest mb-4">Every Month · From $75 / Month</p>
              <ul className="space-y-3">
                {MONTHLY_INCLUDES.map(item => (
                  <li key={item} className="flex items-start gap-3 text-sm text-[#374151]">
                    <Check className="w-4 h-4 text-[#68caf1] flex-shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>
              <p className="text-xs text-[#9CA3AF] mt-4 pt-4 border-t border-gray-100">
                Maintenance visits stay efficient because your car is already at baseline. If something needs extra attention, we handle it — same price.
              </p>
            </>
          ) : (
            <>
              <p className="text-xs font-bold text-[#68caf1] uppercase tracking-widest mb-4">First Visit Only · Base from $200 + Condition Add-Ons</p>
              <ul className="space-y-3">
                {BASELINE_INCLUDES.map(item => (
                  <li key={item} className="flex items-start gap-3 text-sm text-[#374151]">
                    <Check className="w-4 h-4 text-[#68caf1] flex-shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>
              <p className="text-xs text-[#9CA3AF] mt-4 pt-4 border-t border-gray-100">
                This isn't a standard interior detail — it's a full reset. Everything after this is maintenance.
              </p>
            </>
          )}
        </div>
      </section>

      {/* Add-ons */}
      <section className="px-6 pb-10 max-w-3xl mx-auto">
        <h2 className="text-xl font-bold mb-1">Condition Add-Ons</h2>
        <p className="text-[#6B7280] text-sm mb-5">Steam and deep shampoo are already in the base Alchemist Reset. These add-ons address specific conditions — pet hair, stains, odor, car seats.</p>
        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
          {RESET_ADD_ONS.map((addon, i) => (
            <div
              key={addon.label}
              className={`flex items-start gap-3 px-5 py-4 ${i < RESET_ADD_ONS.length - 1 ? 'border-b border-gray-100' : ''}`}
            >
              <span className="text-xl w-7 text-center flex-shrink-0 mt-0.5">{addon.icon}</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#111827]">{addon.label}</p>
                <p className="text-xs text-[#6B7280] leading-relaxed mt-0.5">{addon.desc}</p>
              </div>
              <p className="text-sm font-bold text-[#111827] flex-shrink-0">+${addon.price}</p>
            </div>
          ))}
        </div>
        <p className="text-xs text-[#9CA3AF] mt-3 text-center">Select condition add-ons during booking — included in your 50% deposit calculation.</p>
      </section>

      {/* Phase 1 / Phase 2 */}
      <section className="px-6 pb-10 max-w-3xl mx-auto">
        <div className="border border-gray-200 rounded-2xl overflow-hidden">
          <div className="bg-[#111827] p-5 flex items-center gap-4">
            <div className="w-8 h-8 rounded-full bg-[#68caf1] flex items-center justify-center text-black font-bold text-sm flex-shrink-0">1</div>
            <div className="flex-1">
              <p className="text-white font-bold">Interior Maintenance</p>
              <p className="text-gray-400 text-sm">Vacuum, steam, windows, spot treatment · from $75/mo</p>
            </div>
            <span className="text-[#4ade80] text-xs font-bold uppercase tracking-wide flex-shrink-0">Active</span>
          </div>
          <div className="bg-gray-50 p-5 flex items-center gap-4 border-t border-gray-200">
            <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 font-bold text-sm flex-shrink-0">2</div>
            <div className="flex-1">
              <p className="text-[#6B7280] font-bold">Full Monarch Maintenance</p>
              <p className="text-gray-400 text-sm">Interior + exterior wash, ceramic maintenance · coming soon</p>
            </div>
            <span className="text-[#6B7280] text-xs font-bold uppercase tracking-wide flex-shrink-0">Soon</span>
          </div>
        </div>
        <p className="text-xs text-[#9CA3AF] mt-3 text-center">
          When Phase 2 launches, your plan stays the same — exterior is offered as an upgrade, not a price change.
        </p>
      </section>

      {/* CTAs */}
      <section className="px-6 pb-4 max-w-3xl mx-auto">
        <button
          onClick={() => navigate('/maintenance/book')}
          className="w-full bg-[#68caf1] text-black font-bold text-base py-4 rounded-2xl hover:bg-[#5bb8db] transition-all shadow-lg active:scale-95 touch-manipulation"
        >
          Book the Alchemist Reset — from $200
        </button>
        <p className="text-center text-xs text-[#9CA3AF] mt-2 mb-4">50% deposit today · Sedan $200 · SUV $215 · Truck $225 · Then from $75/month after</p>
        <button
          onClick={() => navigate('/interior-check')}
          className="w-full bg-white border border-gray-200 text-[#111827] font-semibold text-sm py-3.5 rounded-2xl hover:border-[#68caf1] transition-all flex items-center justify-center gap-2 touch-manipulation"
        >
          Not sure what your interior needs? Get a free score
          <ChevronRight className="w-4 h-4 text-[#68caf1]" />
        </button>
      </section>

    </div>
  );
}
